/**
 * @typedef {{ name: string, address?: string, city?: string, province?: string, postal_code?: string, phone?: string, maps_url?: string, hours?: string, latitude?: number, longitude?: number }} StoreData
 * @typedef {{ store: StoreData, distance: number }} NearbyStore
 */

const LOCATION_CACHE_KEY = 'ncn_user_location';
const LOCATION_TTL_MS    = 30 * 24 * 60 * 60 * 1000; // 30 days
const NEARBY_RADIUS_KM   = 10;
const PAGE_SIZE          = 10;

class NcnStoreFinder extends HTMLElement {
  /** @type {{ lat: number, lng: number } | null} */
  _userCoords = null;

  /** @type {'location' | 'city'} */
  _mode = 'location';

  /** @type {StoreData[] | null} */
  _allStores = null;

  /** @type {NearbyStore[]} */
  _nearbyStores = [];

  _locationPage = 1;
  _locationTotalPages = 1;

  _currentPage = 1;
  _totalPages  = 1;
  _currentCity = '';
  _apiUrl      = '';

  connectedCallback() {
    /** @type {Record<string, HTMLElement>} */
    this._refs = {};
    for (const el of this.querySelectorAll('[ref]')) {
      this._refs[el.getAttribute('ref')] = /** @type {HTMLElement} */ (el);
    }

    for (const el of this.querySelectorAll('[on\\:change]')) {
      const method = el.getAttribute('on:change').slice(1);
      el.addEventListener('change', (e) => this[method]?.(e));
    }

    for (const el of this.querySelectorAll('[on\\:click]')) {
      const method = el.getAttribute('on:click').slice(1);
      el.addEventListener('click', (e) => this[method]?.(e));
    }

    this._apiUrl = (this.dataset.api ?? '').replace(/\/+$/, '');
    if (this._apiUrl) {
      this.#fetchCities();
      this.#initGeolocation();
    }
  }

  // ─── Geolocation ────────────────────────────────────────────────────────────

  #initGeolocation() {
    if (!navigator.geolocation) {
      this.#fetchPage(); // fallback: paginated all stores
      return;
    }

    try {
      const cached = localStorage.getItem(LOCATION_CACHE_KEY);
      if (cached) {
        const { lat, lng, expires } = JSON.parse(cached);
        if (Date.now() < expires) {
          this._userCoords = { lat, lng };
          this.#activateLocationMode(lat, lng);
          return;
        }
        localStorage.removeItem(LOCATION_CACHE_KEY);
      }
    } catch { /* ignore */ }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const { latitude: lat, longitude: lng } = coords;
        this._userCoords = { lat, lng };
        try {
          localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify({ lat, lng, expires: Date.now() + LOCATION_TTL_MS }));
        } catch { /* ignore */ }
        this.#activateLocationMode(lat, lng);
      },
      () => this.#fetchPage(), // denied: fallback to paginated
      { timeout: 10000 }
    );
  }

  /** @param {number} lat @param {number} lng */
  async #activateLocationMode(lat, lng) {
    this._mode = 'location';
    this._locationPage = 1;

    const loading = this._refs.loadingState;
    if (loading) loading.hidden = false;

    try {
      if (!this._allStores) {
        const resp = await fetch(`${this._apiUrl}?all=true`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const { stores } = await resp.json();
        this._allStores = stores;
      }

      this._nearbyStores = this._allStores
        .map((store) => {
          const distance = (store.latitude != null && store.longitude != null)
            ? this.#haversine(lat, lng, store.latitude, store.longitude)
            : Infinity;
          return { store, distance };
        })
        .filter(({ distance }) => isFinite(distance) && distance <= NEARBY_RADIUS_KM)
        .sort((a, b) => a.distance - b.distance);

      this._locationTotalPages = Math.max(1, Math.ceil(this._nearbyStores.length / PAGE_SIZE));

      if (loading) loading.hidden = true;
      this.#renderLocationPage();
    } catch {
      if (loading) loading.hidden = true;
      const error = this._refs.errorState;
      if (error) error.hidden = false;
    }
  }

  #renderLocationPage() {
    const list     = this._refs.storeList;
    const template = this._refs.cardTemplate;
    if (!list || !(template instanceof HTMLTemplateElement)) return;

    const start = (this._locationPage - 1) * PAGE_SIZE;
    const page  = this._nearbyStores.slice(start, start + PAGE_SIZE);

    list.innerHTML = '';
    const fragment = document.createDocumentFragment();

    for (const { store, distance } of page) {
      const card = this.#buildCard(template, store);
      if (!card) continue;
      const distEl = /** @type {HTMLElement | null} */ (card.querySelector('.ncn-store-card__distance'));
      if (distEl) {
        distEl.textContent = distance < 1
          ? `${(distance * 1000).toFixed(0)} m away`
          : `${distance.toFixed(1)} km away`;
        distEl.hidden = false;
      }
      fragment.append(card);
    }

    list.append(fragment);

    const total = this._nearbyStores.length;

    if (this._refs.resultCount) {
      this._refs.resultCount.textContent = total > 0
        ? `${total} store${total !== 1 ? 's' : ''} within ${NEARBY_RADIUS_KM} km`
        : '';
    }

    if (this._refs.noResults) {
      this._refs.noResults.hidden = total > 0;
    }

    // Sync pagination refs
    this._currentPage = this._locationPage;
    this._totalPages  = this._locationTotalPages;
    this.#updatePagination();
  }

  // ─── City mode ──────────────────────────────────────────────────────────────

  async #fetchPage() {
    this._mode = 'city';
    const loading = this._refs.loadingState;
    const error   = this._refs.errorState;
    if (loading) loading.hidden = false;
    if (error)   error.hidden   = true;

    try {
      const params = new URLSearchParams({ page: String(this._currentPage), limit: String(PAGE_SIZE) });
      if (this._currentCity) params.set('city', this._currentCity);

      const resp = await fetch(`${this._apiUrl}?${params}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const { stores, page, totalPages, total } = await resp.json();
      this._currentPage = page;
      this._totalPages  = totalPages;

      if (loading) loading.hidden = true;
      this.#renderCityPage(stores, total);
    } catch {
      if (loading) loading.hidden = true;
      if (error)   error.hidden   = false;
    }
  }

  /**
   * @param {StoreData[]} stores
   * @param {number} total
   */
  #renderCityPage(stores, total) {
    const list     = this._refs.storeList;
    const template = this._refs.cardTemplate;
    if (!list || !(template instanceof HTMLTemplateElement)) return;

    list.innerHTML = '';
    const fragment = document.createDocumentFragment();
    for (const store of stores) {
      const card = this.#buildCard(template, store);
      if (card) fragment.append(card);
    }
    list.append(fragment);

    if (this._refs.resultCount) {
      this._refs.resultCount.textContent = this._currentCity
        ? `${total} store${total !== 1 ? 's' : ''} found`
        : '';
    }

    if (this._refs.noResults) {
      this._refs.noResults.hidden = stores.length > 0;
    }

    this.#updatePagination();
  }

  // ─── Event handlers ─────────────────────────────────────────────────────────

  /** @param {Event} _event */
  onCityChange(_event) {
    this._currentCity = /** @type {HTMLSelectElement} */ (this._refs.cityFilter)?.value ?? '';
    this._currentPage = 1;
    // Any dropdown interaction = browse mode (all stores paginated, GPS ignored)
    this.#fetchPage();
  }

  /** @param {Event} _event */
  onPrevPage(_event) {
    if (this._mode === 'location') {
      if (this._locationPage > 1) {
        this._locationPage--;
        this.#renderLocationPage();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      if (this._currentPage > 1) {
        this._currentPage--;
        this.#fetchPage();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  /** @param {Event} _event */
  onNextPage(_event) {
    if (this._mode === 'location') {
      if (this._locationPage < this._locationTotalPages) {
        this._locationPage++;
        this.#renderLocationPage();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      if (this._currentPage < this._totalPages) {
        this._currentPage++;
        this.#fetchPage();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  // ─── Cities dropdown ────────────────────────────────────────────────────────

  async #fetchCities() {
    try {
      const resp = await fetch(`${this._apiUrl}/cities`);
      if (!resp.ok) return;
      const { cities } = await resp.json();
      const select = /** @type {HTMLSelectElement | null} */ (this._refs.cityFilter);
      if (!select) return;
      for (const city of cities) {
        const opt = document.createElement('option');
        opt.value = city;
        opt.textContent = city;
        select.append(opt);
      }
    } catch { /* ignore */ }
  }

  // ─── Card builder ───────────────────────────────────────────────────────────

  /**
   * @param {HTMLTemplateElement} template
   * @param {StoreData} store
   * @returns {HTMLLIElement | null}
   */
  #buildCard(template, store) {
    const clone = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const li    = /** @type {HTMLLIElement | null} */ (clone.firstElementChild);
    if (!li) return null;

    li.dataset.city = (store.city ?? '').toLowerCase();
    li.dataset.lat  = String(store.latitude  ?? '');
    li.dataset.lng  = String(store.longitude ?? '');

    const nameEl = li.querySelector('.ncn-store-card__name');
    if (nameEl) nameEl.textContent = store.name;

    if (store.address || store.city || store.province || store.postal_code) {
      const addressEl = li.querySelector('.ncn-store-card__address');
      if (addressEl) {
        const street   = addressEl.querySelector('.ncn-store-card__street');
        const locality = addressEl.querySelector('.ncn-store-card__locality');
        const postal   = addressEl.querySelector('.ncn-store-card__postal');
        if (street)   street.textContent   = store.address ?? '';
        if (locality) locality.textContent = [store.city, store.province].filter(Boolean).join(', ');
        if (postal)   postal.textContent   = store.postal_code ?? '';
        /** @type {HTMLElement} */ (addressEl).hidden = false;
      }
    }

    if (store.phone) {
      const phoneEl = li.querySelector('.ncn-store-card__phone');
      if (phoneEl) {
        const link  = document.createElement('a');
        link.href   = `tel:${store.phone.replace(/[\s\-\(\)]/g, '')}`;
        link.textContent = store.phone;
        phoneEl.append(link);
        /** @type {HTMLElement} */ (phoneEl).hidden = false;
      }
    }

    if (store.hours) {
      const hoursEl   = li.querySelector('.ncn-store-card__hours');
      const hoursText = li.querySelector('.ncn-store-card__hours-text');
      if (hoursEl && hoursText) {
        hoursText.textContent = store.hours;
        /** @type {HTMLElement} */ (hoursEl).hidden = false;
      }
    }

    if (store.maps_url) {
      const dirEl = /** @type {HTMLAnchorElement | null} */ (li.querySelector('.ncn-store-card__directions'));
      if (dirEl) {
        dirEl.href   = store.maps_url;
        dirEl.hidden = false;
      }
    }

    return li;
  }

  // ─── Pagination UI ──────────────────────────────────────────────────────────

  #updatePagination() {
    const pagination = /** @type {HTMLElement | null} */ (this._refs.pagination);
    if (!pagination) return;

    pagination.hidden = this._totalPages <= 1;

    const pageInfo = this._refs.pageInfo;
    if (pageInfo) pageInfo.textContent = `${this._currentPage} / ${this._totalPages}`;

    const prevBtn = /** @type {HTMLButtonElement | null} */ (this._refs.prevBtn);
    const nextBtn = /** @type {HTMLButtonElement | null} */ (this._refs.nextBtn);
    if (prevBtn) prevBtn.disabled = this._currentPage === 1;
    if (nextBtn) nextBtn.disabled = this._currentPage === this._totalPages;
  }

  // ─── Haversine ──────────────────────────────────────────────────────────────

  /**
   * @param {number} lat1 @param {number} lon1 @param {number} lat2 @param {number} lon2
   * @returns {number} distance in km
   */
  #haversine(lat1, lon1, lat2, lon2) {
    const R    = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a    =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
}

customElements.define('ncn-store-finder', NcnStoreFinder);
